<i18n>
{
    "uk": {
        "defaultTitle": "Що ви отримаєте на курсі?",

        "languageLabel": "Мова&nbsp;викладання | Мова&nbsp;викладання | Мови&nbsp;викладання | Мови&nbsp;викладання",
        "subtitleLabel": "Мова&nbsp;субтитрів | Мова&nbsp;субтитрів | Мови&nbsp;субтитрів | Мови&nbsp;субтитрів",

        "languages": {
            "uk": "українська",
            "ru": "російська",
            "en": "англійська"
        },

        "types": {
            "theoryPractice": "Теорія та&nbsp;практика",
            "theory": "Теорія",
            "practice": "Практика"
        },

        "texts": {
            "language": "{label}: {language}",
            "subtitle": "{label}: {language}",
            "duration": "Час на&nbsp;проходження: {duration}",
            "homework": "Перевірка домашніх робіт",
            "certificate": "Отримання сертифіката",
            "type": "{type}",
            "community": "Підтримка від&nbsp;спільноти студентів",
            "additionalMaterials": "Додаткові матеріали",
            "support": "Служба&nbsp;підтримки CASES"
        }
    },
    "en": {
        "defaultTitle": "What you get on the course",

        "languageLabel": "Video&nbsp;language | Video&nbsp;language | Video&nbsp;languages",
        "subtitleLabel": "Subtitle&nbsp;language | Subtitle&nbsp;language | Subtitle&nbsp;languages",

        "languages": {
            "uk": "Ukrainian",
            "ru": "Russian",
            "en": "English"
        },

        "types": {
            "theoryPractice": "Theory and&nbsp;practice",
            "theory": "Theory",
            "practice": "Practice"
        },

        "texts": {
            "language": "{label}: {language}",
            "subtitle": "{label}: {language}",
            "duration": "Study time: {duration}",
            "homework": "Homework checks",
            "certificate": "Earn a&nbsp;certificate",
            "type": "{type}",
            "community": "Support from&nbsp;the&nbsp;student community",
            "additionalMaterials": "Additional materials",
            "support": "CASES support&nbsp;service"
        }
    }
}
</i18n>


<template>
    <Tiles
        v-if="autoBlock || edit"
        :block="autoBlock || (edit && {})"
        :edit="edit"
        @openTilesEditor="$emit('openFeatureTilesEditor')"
    />
</template>


<script>
    import Tiles from '~/components/creative-practice/course-landing/Tiles.vue';

    import { useCourseLandingStore } from '~/stores/courseLanding';

    import blockPropertyObject from '~/mixins/course-landing/blockPropertyObject.js';

    export default {
        props: {
            block: Object,
            edit:  Boolean,
        },

        components: {
            Tiles,
        },

        mixins: [
            blockPropertyObject,
        ],

        computed: {
            locale () {
                return this.courseLandingStore.locale;
            },

            course () {
                return this.courseLandingStore.course;
            },

            courseLandingStore () {
                return useCourseLandingStore();
            },


            title () {
                return this.blockPropertyObject(this.block.title, 'text');
            },

            content () {
                return this.blockPropertyObject(this.block.content, 'tiles');
            },

            autoBlock () {
                const ownTitle   = this.title;
                const ownContent = this.content;

                if (!('tiles' in ownContent) && !this.edit) return false;

                // Clone
                let block     = this.$structuredClone(this.block);
                block.title   = this.$structuredClone(ownTitle);
                block.content = this.$structuredClone(ownContent);

                if (!ownTitle.text) block.title.text = this.$t('defaultTitle', this.locale);

                block.content.tiles = this.getTiles(ownContent.tiles || []);

                return block;
            },

            courseLocales () {
                if (this.course.locales) {
                    return this.course.locales.filter(item => {
                        return item.audio;
                    }).map(item => item.locale);
                }

                return Object.keys(this.course?.slugMap || {});
            },

            courseSubtitleLocales () {
                if (this.course.locales) {
                    return this.course.locales.filter(item => {
                        return item.subtitles;
                    }).map(item => item.locale);
                }

                return [];
            },
        },

        methods: {
            getTiles (tiles) {
                let ret = [];

                for (const tile of tiles) {
                    // Custom tile
                    if (typeof tile == 'object' && 'icon' in tile && 'text' in tile) {
                        ret.push(tile);
                        continue;
                    }

                    let obj = this.getTile(tile);

                    if (obj) ret.push(obj);
                }

                return ret;
            },

            getTile (opts) {
                if (typeof opts != 'object') opts = { key: opts };

                let text = this.getText(opts);

                if (!text) return false;

                return {
                    icon: this.getIcon(opts),
                    text
                };
            },

            getIcon (opts) {
                const key = opts.key;
                const icons = {
                    language: '/images/creative-practice/about-common/feature-i18n.svg',
                    subtitle: '/images/creative-practice/about-common/feature-i18n.svg',
                    duration: '/images/creative-practice/about-common/feature-calendar.svg',
                    homework: '/images/creative-practice/about-common/feature-homework.svg',
                    certificate: '/images/creative-practice/about-common/feature-certificate.svg',
                    type: '/images/creative-practice/about-common/feature-type.svg',
                    community: '/images/creative-practice/about-common/feature-community.svg',
                    additionalMaterials: '/images/creative-practice/about-common/feature-additional-materials.svg',
                    support: '/images/creative-practice/about-common/feature-support.svg',
                    document: '/images/creative-practice/about-common/feature-document.svg',
                    laptop: '/images/creative-practice/about-common/feature-laptop.svg',
                    graduate: '/images/creative-practice/about-common/feature-graduate.svg',
                    mortarboard: '/images/creative-practice/about-common/feature-mortarboard.svg',
                };

                if (!(key in icons)) {
                    // Default icon
                    return '/images/creative-practice/about-common/feature-homework.svg';
                }

                return icons[key];
            },

            getText (opts) {
                const key = opts.key;

                if (key == 'language') {
                    return this.getLanguageText(opts);
                }

                if (key == 'subtitle') {
                    return this.getSubtitleText(opts);
                }

                if (key == 'duration') {
                    return this.getDurationText(opts);
                }

                if (key == 'type') {
                    return this.getTypeText(opts);
                }

                return this.$t(`texts.${key}`, this.locale);
            },

            getLanguageText (opts) {
                return this.getLocaleText(opts, {
                    source: this.courseLocales,
                    label:  'languageLabel',
                    text:   'texts.language'
                });
            },

            getSubtitleText (opts) {
                return this.getLocaleText(opts, {
                    source: this.courseSubtitleLocales,
                    label:  'subtitleLabel',
                    text:   'texts.subtitle'
                });
            },

            getLocaleText (opts, values) {
                const { source, label, text } = values;

                let locales = opts.locales || source;

                if (locales.length < 1) return false;

                let labelText = this.$tc(label, locales.length, this.locale);
                let names     = locales.map(loc => {
                    return this.$t(`languages.${loc}`, this.locale);
                });

                return this.$t(text, this.locale, {
                    label:    labelText,
                    language: names.join(', '),
                });
            },

            getDurationText (opts) {
                // TODO: calculate duration from program
                let duration = opts.duration;

                return this.$t('texts.duration', this.locale, {
                    duration,
                });
            },

            getTypeText (opts) {
                let type = opts.type;

                if (!type) {
                    const hasTheory   = this.course.moduleTypes.includes('theory');
                    const hasPractice = this.course.moduleTypes.includes('practice');

                    if (!hasTheory && !hasPractice) return false;

                    if (hasTheory && hasPractice) {
                        type = 'theoryPractice';
                    } else if (hasTheory) {
                        type = 'theory';
                    } else if (hasPractice) {
                        type = 'practice';
                    }
                }

                if (!['theoryPractice', 'theory', 'practice'].includes(type)) {
                    // Custom type
                    return type;
                }

                let name = this.$t(`types.${type}`, this.locale);

                return this.$t('texts.type', this.locale, {
                    type: name,
                });
            },
        }
    }
</script>
